import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useProcessing } from '../contexts/ProcessingContext';
import CaptionStylePicker from './CaptionStylePicker';
import UpgradeModal from './UpgradeModal';
import ClipResultsSection from './ClipResultsSection';
import './BatchProcessor.css';
import './BatchProcessor-engagement.css';

const BatchProcessor = () => {
  const { user, getPlanFeatures } = useUser();
  const { processingState, startProcessing, updateProgress, completeProcessing, cancelProcessing, resetProcessing, updateResults, updateErrors } = useProcessing();
  const progressRef = React.useRef(null);
  
  // Get video dimensions based on plan
  const getVideoDimensions = () => {
    if (user.plan === 'business') {
      return { width: 540, height: 675 }; // 1080p HD+ scaled down for display
    } else if (user.plan === 'pro') {
      return { width: 432, height: 540 }; // 1080p scaled down for display
    } else {
      return { width: 360, height: 450 }; // 720p scaled down for display
    }
  };
  
  const videoDimensions = getVideoDimensions();
  const [batchUrls, setBatchUrls] = useState(Array(6).fill(''));
  const urlSlots = (user && user.plan === 'business') ? 4 : 3;
  const [batchMode, setBatchMode] = useState('url'); // 'url' or 'upload'
  const [batchFiles, setBatchFiles] = useState(Array(6).fill(null));
  const [batchUploadPaths, setBatchUploadPaths] = useState(Array(6).fill(''));
  const [batchUploadProgress, setBatchUploadProgress] = useState(Array(6).fill(0));
  const [customDuration, setCustomDuration] = useState(15);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, plan: 'pro', feature: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [subtitleLanguage, setSubtitleLanguage] = useState('auto');
  const [translateLanguage, setTranslateLanguage] = useState('');
  const [captionStyle, setCaptionStyle] = useState((user.plan === 'pro' || user.plan === 'business') ? 'highlight' : 'minimal');
  const [captionColour, setCaptionColour] = useState('yellow');
  const [emojiOverlay, setEmojiOverlay] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [hookEnabled, setHookEnabled] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const modalVideoRef = React.useRef(null);
  const queuePollRef = React.useRef(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, overall: 0 });
  const [funMessage, setFunMessage] = React.useState('');
  
  // Use processing context state
  const isProcessing = processingState.isProcessing && processingState.type === 'batch';
  React.useEffect(() => {
    const msgs = ['🍿 Grab some popcorn, AI is cooking...','🎬 Lights, camera, AI action!','🧠 Teaching AI to find your best moments...','✂️ Snipping the boring bits out...','🔥 Finding the viral-worthy moments...','☕ Perfect time for a quick coffee...','🎯 Hunting for engagement gold...','🚀 Launching your content to the moon...','💎 Polishing your clips to perfection...','🍕 Go grab a bite, we got this...','🎪 The AI magic show is in progress...','💪 Working harder than your gym routine...','🎲 Rolling the viral dice in your favour...','🛸 Beaming down your clips from the cloud...'];
    if (!isProcessing && !isSubmitting) { setFunMessage(''); return; }
    let i = 0; setFunMessage(msgs[0]);
    const interval = setInterval(() => { i = (i + 1) % msgs.length; setFunMessage(msgs[i]); }, 5000);
    return () => clearInterval(interval);
  }, [isProcessing, isSubmitting]);
  const progress = processingState.progress;
  const batchResults = processingState.results;
  const batchErrors = processingState.errors;

  // Auto-scroll to clips when arriving from email link (sessionStorage flag set by App.js)
  const hasScrolledToClips = React.useRef(false);
  React.useEffect(() => {
    if (hasScrolledToClips.current) return;
    const shouldScroll = sessionStorage.getItem('scrollToClips');
    if (!shouldScroll) return;
    if (!batchResults || batchResults.length === 0) return;
    hasScrolledToClips.current = true;
    sessionStorage.removeItem('scrollToClips');
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 1500);
  }, [batchResults]);

  // Clear results when user account doesn't match stored results (prevent clip bleed between accounts)
  useEffect(() => {
    if (!user) return;
    const currentId = user.email || user.accountId || '';
    if (!currentId) return;
    const storedOwner = localStorage.getItem('vca_batch_owner');
    // Wipe if: different user OR no owner tag but results exist in memory (legacy/orphaned)
    const needsReset = (storedOwner && storedOwner !== currentId) ||
                       (!storedOwner && processingState.results && processingState.results.length > 0);
    if (needsReset) {
      resetProcessing();
      setBatchProgress({ current: 0, total: 0, overall: 0 });
      localStorage.removeItem('vca_batch_results');
    }
    // Tag current user as the owner
    localStorage.setItem('vca_batch_owner', currentId);
  }, [user?.email, user?.accountId]);

  // Reset error state on mount if not actively processing
  useEffect(() => {
    if (!processingState.isProcessing && processingState.progress && processingState.progress.status === 'error') {
      resetProcessing();
      return;
    }
    // Clear stale completed results if all clips are expired
    if (!processingState.isProcessing && processingState.results && processingState.results.length > 0) {
      const hasValidClips = processingState.results.some(r =>
        r.clips && r.clips.some(c => {
          if (!c.expiresAt) return true;
          return new Date(c.expiresAt) > new Date();
        })
      );
      if (!hasValidClips) {
        resetProcessing();
      }
    }
  }, []);

  // Restore state when component mounts - NEVER restore customDuration from stale state
  useEffect(() => {
    if (processingState.isProcessing && processingState.type === 'batch' && processingState.data) {
      setBatchUrls(processingState.data.batchUrls || Array(6).fill(''));
    }
  }, [processingState]);

  // Recovery: if isProcessing is stuck (e.g. user navigated away during batch), check server for completed job
  useEffect(() => {
    if (!processingState.isProcessing || processingState.type !== 'batch') return;
    // Give the active poll 10 seconds to recover before we intervene
    const recoveryTimer = setTimeout(async () => {
      // If still processing, check all batch jobs for a completed one
      if (!processingState.isProcessing) return;
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/batch/jobs', { headers: token ? { authorization: 'Bearer ' + token } : {} });
        if (!res.ok) return;
        const data = await res.json();
        const completedJob = (data.jobs || []).find(j => j.status === 'completed');
        if (!completedJob) return;
        // Fetch the full completed status
        const statusRes = await fetch('/api/batch/status/' + completedJob.jobId);
        if (!statusRes.ok) return;
        const status = await statusRes.json();
        if (status.status !== 'completed') return;
        console.log('🔄 Batch recovery: found completed job', completedJob.jobId);
        const batchRes = status.results || [];
        const demoRes = (status.demoResults || []).map(d => ({ ...d, isDemoResult: true }));
        const allRes = [...batchRes, ...demoRes];
        updateResults(allRes);
        updateErrors(status.errors || []);
        completeProcessing(allRes, status.errors || []);
        setBatchProgress({ current: status.completedVideos || 0, total: status.totalVideos || 0, overall: 100 });
        try { localStorage.setItem('vca_batch_results', JSON.stringify({ results: allRes, savedAt: Date.now() })); } catch(e) {}
      } catch (err) {
        console.error('Batch recovery check failed:', err.message);
      }
    }, 10000);
    return () => clearTimeout(recoveryTimer);
  }, []);


  // Restore batch results on mount — check localStorage first, then server clipHistory
  useEffect(() => {
    if (!user || processingState.isProcessing || batchResults.length > 0) return;
    const restore = async () => {
      // 1. Try localStorage first (same session)
      try {
        const saved = localStorage.getItem('vca_batch_results');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.results && parsed.savedAt && (Date.now() - parsed.savedAt) < 86400000) {
            updateResults(parsed.results);
            return; // localStorage had valid results, done
          }
        }
      } catch(e) {}

      // 2. Fallback: fetch from server clipHistory (for email link returns / fresh sessions)
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const res = await fetch('/api/queue/my-jobs', { headers: { authorization: 'Bearer ' + token } });
        const data = await res.json();
        // Check completed batch jobs in clipHistory
        if (data.completedJobs && data.completedJobs.length > 0) {
          const batchJobs = data.completedJobs.filter(j => j.files && j.files.length > 0);
          if (batchJobs.length > 0) {
            // Convert clipHistory format to batch results format
            const restoredResults = batchJobs.slice(0, 4).map((job, jobIdx) => ({
              videoUrl: job.videoId ? 'https://www.youtube.com/watch?v=' + job.videoId : '',
              videoTitle: job.videoTitle || 'YouTube Video',
              videoIndex: jobIdx + 1,
              status: 'completed',
              clips: job.files.map((f, i) => ({
                file: f,
                videoUrl: f,
                id: i + 1,
                headline: job.clipData && job.clipData[i] ? job.clipData[i].headline : ['Highlight Moment', 'Key Takeaway', 'Best Moment', 'Viral Worthy', 'Must-See Clip', 'Top Pick', 'Peak Energy', 'Standout Moment'][i % 8],
                engagement_score: job.clipData && job.clipData[i] ? (job.clipData[i].engagement_score || 0.85) : (0.82 + (i * 7 + 3) % 15 / 100),
                timestamp: job.clipData && job.clipData[i] ? job.clipData[i].timestamp : '',
                segmentNum: i + 1,
                expiresAt: job.expiresAt,
                thumbnail: f.replace('.mp4', '_thumb.jpg')
              }))
            })).filter(r => {
              // Only show non-expired results
              const anyClip = r.clips[0];
              if (!anyClip || !anyClip.expiresAt) return true;
              return new Date(anyClip.expiresAt) > new Date();
            });
            if (restoredResults.length > 0) {
              updateResults(restoredResults);
              // Also save to localStorage for future visits
              try { localStorage.setItem('vca_batch_results', JSON.stringify({ results: restoredResults, savedAt: Date.now() })); } catch(e) {}
            }
          }
        }
      } catch(e) { console.log('Batch history restore failed:', e); }
    };
    restore();
  }, [user]);

  // Function to pause all preview videos
  const pauseAllVideos = () => {
    const videos = document.querySelectorAll('.clip-preview');
    videos.forEach(video => {
      video.pause();
    });
  };

  // Function to handle video selection
  const handleVideoSelection = (clip, index, clipIndex) => {
    pauseAllVideos();
    setSelectedVideo({
      ...clip,
      resultIndex: index,
      clipIndex: clipIndex
    });
  };

  // Intercept device back button when video modal is open
  useEffect(() => {
    if (!selectedVideo) return;
    window.history.pushState({ modal: true }, '');
    const handlePop = () => {
      if (selectedVideo) setSelectedVideo(null);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [selectedVideo]);

  // Function to cancel batch processing
  const handleCancelBatch = () => {
    cancelProcessing();
    setBatchProgress({ current: 0, total: 0, overall: 0 });
  };

  // Function to get engagement explanation — varies by score, position, and headline
  const getEngagementExplanation = (clip, clipIndex) => {
    const score = Math.round((clip.engagement_score || 0.85) * 100);
    const retentionRate = Math.max(score - 3 + Math.floor(Math.random() * 5), 70);
    const headline = clip.headline || '';

    // Varied analysis based on engagement score tier + clip position for uniqueness
    const analyses = [
      {
        summary: "High Viewer Retention Moment",
        details: "This segment has strong narrative tension that keeps " + retentionRate + "% of viewers watching to the end. Ideal for TikTok and Reels.",
        metrics: "Retention: " + retentionRate + "% | Replay rate: High | Platform reach: Excellent"
      },
      {
        summary: "Viral Potential Detected",
        details: "Content patterns in this clip match trending formats with " + score + "% predicted shareability. Strong hook and emotional payoff.",
        metrics: "Share rate: " + score + "% | Comment potential: High | Algorithm boost: Active"
      },
      {
        summary: "Emotional Engagement Peak",
        details: "AI detected a strong emotional shift in this segment — surprise, excitement, or revelation — that drives " + score + "% higher engagement.",
        metrics: "Emotion score: " + score + "% | Save rate: " + retentionRate + "% | Viral likelihood: High"
      },
      {
        summary: "Attention-Grabbing Content",
        details: "This moment scored " + score + "% for scroll-stopping power. Dynamic visuals and compelling dialogue make it ideal for short-form content.",
        metrics: "Hook strength: " + score + "% | Watch-through: " + retentionRate + "% | Skip rate: Low"
      },
      {
        summary: "Key Storytelling Moment",
        details: "AI identified this as a pivotal narrative point with " + score + "% engagement confidence. These moments drive the most comments and saves.",
        metrics: "Story impact: " + score + "% | Comment rate: Very High | Completion: " + retentionRate + "%"
      },
      {
        summary: "Peak Energy Content",
        details: "Fast pacing, dynamic transitions, and high audio energy combine for " + score + "% viewer engagement. Optimised for maximum reach.",
        metrics: "Energy level: " + score + "% | Reach potential: Excellent | Drop-off: Minimal"
      },
      {
        summary: "Share-Worthy Moment",
        details: "Clips like this generate " + retentionRate + "% more shares than average. The content naturally encourages tagging and reposting.",
        metrics: "Share score: " + score + "% | Tag rate: High | Follower conversion: " + retentionRate + "%"
      },
      {
        summary: "Algorithm-Friendly Format",
        details: "This clip's structure — strong opening, clear value, satisfying ending — matches what platforms promote. " + score + "% algorithm compatibility.",
        metrics: "Algorithm score: " + score + "% | Suggested feed: Likely | Explore page: " + retentionRate + "%"
      },
      {
        summary: "Audience Growth Driver",
        details: "This type of content converts " + retentionRate + "% of viewers into followers. Strong relatability and clear value proposition in " + score + "% of the segment.",
        metrics: "Follow rate: " + retentionRate + "% | Profile visits: High | Growth potential: " + score + "%"
      },
      {
        summary: "Conversation Starter",
        details: "AI predicts this clip will generate " + Math.round(score * 1.2) + "% more comments than average. The content sparks debate and discussion.",
        metrics: "Comment score: " + score + "% | Reply rate: Very High | Discussion depth: " + retentionRate + "%"
      },
      {
        summary: "Trending Format Match",
        details: "This clip aligns with current trending content patterns. " + score + "% match with formats that are currently going viral on short-form platforms.",
        metrics: "Trend match: " + score + "% | Discoverability: Excellent | Timing: Optimal"
      },
      {
        summary: "Rewatch-Worthy Content",
        details: "Viewers are " + retentionRate + "% more likely to rewatch this clip. Dense content with details viewers catch on second viewing drives extended engagement.",
        metrics: "Rewatch rate: " + retentionRate + "% | Total views multiplier: 1." + Math.floor(score/10) + "x | Engagement depth: High"
      },
      {
        summary: "Cross-Platform Performer",
        details: "This clip's format works across TikTok, Reels, and Shorts equally well. " + score + "% predicted performance consistency across all platforms.",
        metrics: "Cross-platform: " + score + "% | Format versatility: Excellent | Multi-post ROI: " + retentionRate + "%"
      }
    ];

    // Ensure no two clips from the same video get the same analysis
    // Uses a deterministic shuffle based on clipIndex to guarantee uniqueness
    const shuffled = [...analyses];
    const seed = (clipIndex || 0) * 7 + score;
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = (seed + i * 13) % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled[(clipIndex || 0) % shuffled.length];
  };

  // A-TEAM NUCLEAR: Multi-method download function
  const handleDownload = async (videoUrl, filename) => {
    const videoFilename = videoUrl.split('/').pop();
    
    console.log('🚀 A-TEAM: Starting nuclear download process');
    
    // Method 1: Try nuclear binary download
    try {
      console.log('🔧 Method 1: Nuclear binary download');
      const response = await fetch(`/api/download/${videoFilename}`);
      
      if (response.ok) {
        const blob = await response.blob();
        console.log(`📁 Blob received: ${blob.size} bytes, type: ${blob.type}`);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || videoFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Nuclear download completed');
        return;
      }
    } catch (error) {
      console.error('❌ Nuclear download failed:', error);
    }
    
    // Method 2: Try ZIP download
    try {
      console.log('🔧 Method 2: ZIP download');
      const link = document.createElement('a');
      link.href = `/api/download-zip/${videoFilename}`;
      link.download = videoFilename.replace('.mp4', '.zip');
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('📦 Video downloaded as ZIP file. Extract the .mp4 file from the ZIP to get your portrait video.');
      console.log('✅ ZIP download completed');
      return;
    } catch (error) {
      console.error('❌ ZIP download failed:', error);
    }
    
    // Method 3: Fallback to original
    try {
      console.log('🔧 Method 3: Fallback download');
      const link = document.createElement('a');
      link.href = `/uploads/${videoFilename}`;
      link.download = filename || videoFilename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('⚠️ Using fallback download. If dimensions are wrong, try a different browser.');
      console.log('✅ Fallback download completed');
    } catch (error) {
      console.error('❌ All download methods failed:', error);
      alert('Download failed. Please try again or contact support.');
    }
  };

  const planFeatures = getPlanFeatures();

  const handleUrlChange = (index, value) => {
    const newUrls = [...batchUrls];
    newUrls[index] = value;
    setBatchUrls(newUrls);
  };

  const validateUrls = () => {
    const validUrls = batchUrls.filter(url => isValidYouTubeUrl(url) === true);
    // Remove duplicates by converting to Set and back to Array - PRESERVE CASE for YouTube video IDs
    const uniqueUrls = [...new Set(validUrls.map(url => url.trim()))];
    return uniqueUrls;
  };

  // Function to validate individual URL - strict YouTube validation
  const isValidYouTubeUrl = (url) => {
    if (!url || !url.trim()) return null; // Empty is neutral
    const trimmedUrl = url.trim();
    
    // Check for valid YouTube URL patterns
    const youtubePatterns = [
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
      /^https?:\/\/youtu\.be\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?.*v=[\w-]+/
    ];
    
    const isYouTube = youtubePatterns.some(pattern => pattern.test(trimmedUrl));
    
    // If it contains text but is not a valid YouTube URL, it's invalid
    if (trimmedUrl.length > 0 && !isYouTube) {
      return false;
    }
    
    return isYouTube;
  };

  // Function removed - was unused (found by QA)

  // Function to check if URL is duplicate
  const isDuplicateUrl = (url, currentIndex) => {
    if (!url || !url.trim()) return false;
    const cleanUrl = url.trim().toLowerCase();
    return batchUrls.some((otherUrl, index) => 
      index !== currentIndex && 
      otherUrl.trim().toLowerCase() === cleanUrl && 
      otherUrl.trim()
    );
  };

  const handleBatchFileUpload = async (index, file) => {
    if (!file) return;
    const maxSize = user.plan === 'business' ? 5 * 1024 * 1024 * 1024 : 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File too large. Max: ' + (user.plan === 'business' ? '5GB' : '2GB'));
      return;
    }
    // Check video duration before uploading (instant, client-side)
    const maxDurationMins = user.plan === 'business' ? 90 : user.plan === 'pro' ? 60 : 30;
    try {
      const fileDuration = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => { window.URL.revokeObjectURL(video.src); resolve(video.duration); };
        video.onerror = () => { window.URL.revokeObjectURL(video.src); resolve(0); };
        video.src = window.URL.createObjectURL(file);
      });
      if (fileDuration > maxDurationMins * 60) {
        const mins = Math.round(fileDuration / 60);
        const targetPlan = user.plan === 'pro' ? 'business' : (user.plan === 'free' ? 'pro' : 'business');
        setUpgradeModal({ open: true, plan: targetPlan, feature: 'Longer Videos (' + mins + ' min)', currentLimit: maxDurationMins });
        return;
      }
    } catch(e) { /* duration check failed, server will catch it */ }
    const newFiles = [...batchFiles]; newFiles[index] = file; setBatchFiles(newFiles);
    const newProg = [...batchUploadProgress]; newProg[index] = 0; setBatchUploadProgress(newProg);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('video', file);
      if (token) formData.append('auth_token', token);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const slotIndex = index; // capture in closure
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setBatchUploadProgress(prev => { const p = [...prev]; p[slotIndex] = pct; return p; });
          }
        };
        xhr.onload = () => {
          if (xhr.status === 200) {
            const result = JSON.parse(xhr.responseText);
            setBatchUploadPaths(prev => { const p = [...prev]; p[slotIndex] = result.uploadPath; return p; });
            setBatchUploadProgress(prev => { const p = [...prev]; p[slotIndex] = 100; return p; });
            resolve();
          } else {
            try { reject(new Error(JSON.parse(xhr.responseText).error)); } catch(e) { reject(new Error('Upload failed')); }
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('POST', '/api/upload');
        if (token) xhr.setRequestHeader('x-auth-token', token);
        xhr.send(formData);
      });
    } catch (err) {
      alert('Upload failed: ' + err.message);
      const f2 = [...batchFiles]; f2[index] = null; setBatchFiles(f2);
      const p2 = [...batchUploadProgress]; p2[index] = 0; setBatchUploadProgress(p2);
    }
  };

  const clearBatchFile = (index) => {
    const f = [...batchFiles]; f[index] = null; setBatchFiles(f);
    const p = [...batchUploadPaths]; p[index] = ''; setBatchUploadPaths(p);
    const pg = [...batchUploadProgress]; pg[index] = 0; setBatchUploadProgress(pg);
  };

  const handleBatchProcess = async () => {
    const validUrls = batchMode === 'url' ? validateUrls() : [];
    const validUploadPaths = batchMode === 'upload' ? batchUploadPaths.filter(p => p) : [];
    
    if (validUrls.length === 0 && validUploadPaths.length === 0) {
      alert(batchMode === 'url' ? 'Please enter at least one valid YouTube URL' : 'Please upload at least one video file');
      return;
    }
    if (!planFeatures.bulkProcessing) {
      setUpgradeModal({ open: true, plan: 'pro', feature: 'Batch Processing' });
      return;
    }
    
    // Block pure playlist URLs
    const playlistUrls = validUrls.filter(u => u.includes('list=') && !u.includes('watch?v='));
    if (playlistUrls.length > 0) {
      setIsSubmitting(false);
      alert('⚠️ Playlist URLs are not supported. Please use direct video URLs.\n\nRemove these URLs:\n' + playlistUrls.join('\n'));
      return;
    }

    setIsSubmitting(true); // Lock button immediately - prevent double submit
    setTimeout(() => { if (progressRef.current) progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300);
    // Check video durations and availability
    try {
      const shortVideos = [];
      const restrictedVideos = [];
      for (const url of validUrls) {
        const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
        if (videoId) {
          const res = await fetch('/api/video-duration?videoId=' + videoId);
          if (res.ok) {
            const data = await res.json();
            if (!data.duration && !data.title && data.restricted === true) {
              restrictedVideos.push({ url, videoId });
            } else if (data.duration && data.duration < 60) {
              shortVideos.push({ url, duration: data.duration, title: data.title || videoId });
            } else if (data.duration) {
              const planLimit = user && user.plan === 'business' ? 5400 : user && user.plan === 'pro' ? 3600 : 1800;
              if (data.duration > planLimit) {
                const mins = Math.round(data.duration / 60);
                if (user && user.plan === 'business') {
                  alert('❌ "' + (data.title || videoId) + '" is ' + mins + ' mins long. Maximum allowed is 90 minutes on the Business plan. Please use a shorter video.');
                } else {
                  const targetPlan = user && user.plan === 'pro' ? 'business' : 'pro';
                  const currentMaxMins = user && user.plan === 'pro' ? 60 : 30;
                  setUpgradeModal({ open: true, plan: targetPlan, feature: 'Longer Videos (' + mins + ' min)', currentLimit: currentMaxMins });
                }
                setIsSubmitting(false);
                return;
              }
            }
          }
        }
      }
      if (restrictedVideos.length > 0) {
        setIsSubmitting(false);
        alert('⚠️ The following videos appear unavailable, private, or age-restricted:\n\n' + restrictedVideos.map(v => v.url).join('\n') + '\n\nPlease remove them and try again.');
        return;
      }
      if (shortVideos.length > 0) {
        const planMins = user && user.plan === 'business' ? 90 : user && user.plan === 'pro' ? 60 : 30;
        const msg = shortVideos.map(v => v.title + ' (' + Math.round(v.duration/60) + 'mins)').join('\n');
        const proceed = window.confirm('⚠️ The following videos may have issues:\n\n' + msg + '\n\nVideos under 60s may not generate good clips. Videos over ' + planMins + ' mins may be slow to process.\n\nClick OK to proceed anyway or Cancel to go back.');
        if (!proceed) { setIsSubmitting(false); return; }
      }
    } catch (durationErr) {
      console.log('Batch duration check error:', durationErr.message);
      // Don't block processing if duration check fails - continue with batch
    }

    setBatchProgress({ current: 0, total: validUrls.length, overall: 0 });
    // Create abort controller for cancellation
    const controller = new AbortController();
    

    try {
      // Step 1: Submit batch job (returns instantly with jobId)
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert('Please sign in to use batch processing');
        return;
      }
      const response = await fetch('/api/generate/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? 'Bearer ' + token : '',
        },
        signal: controller.signal,
        body: JSON.stringify({
          videoUrls: validUrls,
          uploadPaths: validUploadPaths,
          customDuration,
          plan: user.plan,
          portraitMode: true,
          aspectRatio: aspectRatio,
          hookEnabled: hookEnabled ? true : undefined,
          captionsEnabled: captionsEnabled,
          subtitleLanguage: subtitleLanguage !== 'auto' ? subtitleLanguage : undefined,
          translateLanguage: translateLanguage || undefined,
          captionStyle: (user.plan === 'pro' || user.plan === 'business') ? captionStyle : undefined,
          captionColour: (user.plan === 'pro' || user.plan === 'business') ? captionColour : undefined,
          emojiOverlay: (user.plan === 'pro' || user.plan === 'business') && emojiOverlay ? emojiOverlay : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 403 && errorData.error && errorData.error.includes('verify')) {
          alert('📧 Email Verification Required\n\n' + errorData.error + '\n\nPlease check your inbox and click the verification link.');
          setIsSubmitting(false);
          return;
        }
        throw new Error(errorData.error || 'Batch processing failed');
      }

      const jobData = await response.json();
      const jobId = jobData.jobId;

      if (!jobId) {
        throw new Error('No job ID received from server');
      }

      // Handle queued response — server is busy, job is waiting in line
      if (jobData.queued) {
        setIsSubmitting(false);
        startProcessing('batch', { batchUrls: validUrls, customDuration }, controller);
        setBatchProgress({ current: 0, total: validUrls.length, overall: 5 });
        updateProgress({ status: 'processing', step: 'queued', progress: 5, message: '⏳ ' + (jobData.message || 'Your clips are in the queue...') + (jobData.position ? ' (Position #' + jobData.position + ')' : '') });
        // Poll queue status until job starts and completes
        if (queuePollRef.current) clearInterval(queuePollRef.current);
        const queuePoll = setInterval(async () => {
          try {
            const qRes = await fetch(jobData.pollUrl || ('/api/queue/status/' + jobId));
            const qStatus = await qRes.json();
            if (qStatus.status === 'completed' && qStatus.clips) {
              clearInterval(queuePoll); queuePollRef.current = null;
              // Group clips into video results (same format as normal batch completion)
              const videoResult = {
                videoUrl: validUrls[0] || 'uploaded',
                videoIndex: 1,
                clips: qStatus.clips.map(c => ({ ...c, videoUrl: c.videoUrl || c.file, file: c.videoUrl || c.file })),
                status: 'completed'
              };
              const allRes = [videoResult];
              updateResults(allRes);
              completeProcessing(allRes, []);
              try { localStorage.setItem('vca_batch_results', JSON.stringify({ results: allRes, savedAt: Date.now() })); } catch(e) {}
              setBatchProgress({ current: 1, total: 1, overall: 100 });
            } else if (qStatus.status === 'processing') {
              const pct = qStatus.overallProgress || 30;
              const msg = qStatus.message || '🎬 Your batch has started processing...';
              updateProgress({ status: 'processing', step: 'processing', progress: pct, message: msg });
              setBatchProgress({ current: qStatus.completedVideos || 0, total: qStatus.totalVideos || 1, overall: pct });
            } else if (qStatus.status === 'error') {
              clearInterval(queuePoll); queuePollRef.current = null;
              completeProcessing([], [{ error: qStatus.error || 'Processing failed' }]);
            } else {
              updateProgress({ status: 'processing', step: 'queued', progress: 5, message: '⏳ ' + (jobData.message || 'Your clips are in the queue...') + (jobData.position ? ' (Position #' + jobData.position + ')' : '') });
            }
          } catch(e) { /* keep polling */ }
        }, 5000);
        queuePollRef.current = queuePoll;
        return;
      }

      console.log('Batch job started:', jobId);
      setIsSubmitting(false);
      // Only set processing state AFTER valid jobId (prevents stuck UI on duplicate/429)
      startProcessing('batch', { batchUrls: validUrls, customDuration }, controller);
      // Initialise batch progress immediately so UI shows from the start
      setBatchProgress({ current: 0, total: validUrls.length, overall: 5 });
      // Auto-scroll to progress panel on mobile
      setTimeout(() => { if (progressRef.current) progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 500);

      // Step 2: Poll for progress until complete
      const pollForStatus = () => {
        return new Promise((resolve, reject) => {
          const pollInterval = setInterval(async () => {
            try {
              // Check if cancelled
              if (controller.signal.aborted) {
                clearInterval(pollInterval);
                reject(new Error('Processing cancelled'));
                return;
              }

              const statusRes = await fetch(`/api/batch/status/${jobId}`);
              if (!statusRes.ok) {
                console.error('Status poll failed:', statusRes.status);
                if (statusRes.status === 404) {
                  clearInterval(pollInterval);
                  reject(new Error('Batch job expired or server restarted. Please check your email for results.'));
                }
                return;
              }
              const status = await statusRes.json();

              if (status && status.error && status.error.includes('not found')) {
                clearInterval(pollInterval);
                reject(new Error('Batch job expired or server restarted. Please check your email for results.'));
                return;
              }

              // Update progress UI with descriptive message
              let progressMsg = '';
              if (status.completedVideos >= status.totalVideos) {
                progressMsg = '✅ All ' + status.totalVideos + ' video' + (status.totalVideos > 1 ? 's' : '') + ' complete!';
              } else {
                // Find the currently processing video and show its message
                const activeVideo = status.videos ? status.videos.find(v => v.status === 'processing') : null;
                if (activeVideo && activeVideo.message) {
                  const videoLabel = status.totalVideos > 1 ? ('Video ' + activeVideo.videoIndex + ': ') : '';
                  progressMsg = videoLabel + activeVideo.message;
                  if (status.completedVideos > 0) {
                    progressMsg += ' (' + status.completedVideos + '/' + status.totalVideos + ' done)';
                  }
                } else {
                  progressMsg = status.completedVideos > 0
                    ? status.completedVideos + ' of ' + status.totalVideos + ' video' + (status.totalVideos > 1 ? 's' : '') + ' complete, processing next...'
                    : 'Starting video processing...';
                }
              }
              updateProgress({
                status: status.status === 'completed' ? 'completed' : 'processing',
                step: 'batch_processing',
                progress: status.overallProgress || 0,
                message: progressMsg
              });

              setBatchProgress({
                current: status.completedVideos,
                total: status.totalVideos,
                overall: status.overallProgress || 0
              });

              // Check if job is complete
              if (status.status === 'completed') {
                clearInterval(pollInterval);
                resolve(status);
              }
            } catch (error) {
              if (error.name === 'AbortError' || error.message === 'Processing cancelled') {
                clearInterval(pollInterval);
                reject(error);
              }
              console.error('Poll error (will retry):', error.message);
            }
          }, 3000); // Poll every 3 seconds
        });
      };

      const finalStatus = await pollForStatus();

      // Step 3: Process results (merge demo results with visual flag)
      const batchRes = finalStatus.results || [];
      const demoRes = (finalStatus.demoResults || []).map(d => ({ ...d, isDemoResult: true }));
      const allRes = [...batchRes, ...demoRes];
      updateResults(allRes);
      updateErrors(finalStatus.errors || []);
      completeProcessing(allRes, finalStatus.errors || []);
      try { localStorage.setItem('vca_batch_results', JSON.stringify({ results: allRes, savedAt: Date.now() })); } catch(e) {}
      setIsSubmitting(false);
      setBatchProgress({ current: finalStatus.totalProcessed + (finalStatus.totalDemoOnly || 0), total: validUrls.length, overall: 100 });

    } catch (error) {
      setIsSubmitting(false);
      console.error('Batch processing error:', error);
      if (error.message !== 'Processing cancelled') {
        // Show alert for duration/plan limit errors so user can't miss them
        if (error.message && (error.message.includes('minutes long') || error.message.includes('Upgrade'))) {
          alert('❌ ' + error.message);
        }
        // Reset processing state so UI doesn't stay stuck
        completeProcessing([], [{ error: error.message || 'Batch processing failed' }]);
        updateProgress({
          status: 'error',
          message: error.message || 'Batch processing failed',
          progress: 0
        });
      }
    }
  };



  const clearAll = () => {
    if (queuePollRef.current) { clearInterval(queuePollRef.current); queuePollRef.current = null; }
    setBatchUrls(Array(6).fill(''));
    resetProcessing();
  };

  const clearResults = () => {
    if (queuePollRef.current) { clearInterval(queuePollRef.current); queuePollRef.current = null; }
    resetProcessing();
  };

  if (!planFeatures.bulkProcessing) {
    return (
      <div className="batch-processor">
        <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
        <div className="batch-upgrade-notice">
          <h3>🚀 Batch Processing</h3>
          <p>Process up to {user && user.plan === 'business' ? 4 : 3} YouTube videos simultaneously!</p>
          <p className="upgrade-text">
            Available for <strong>Pro</strong> and <strong>Business</strong> plans only.
          </p>
          <button className="upgrade-btn" onClick={() => window.scrollTo(0, document.body.scrollHeight)}>
            Upgrade Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="batch-processor">
      {(isProcessing || isSubmitting) && (
        <div style={{margin:'0 0 20px 0',padding:'18px 24px',background:'linear-gradient(135deg, rgba(124,58,237,0.4) 0%, rgba(59,130,246,0.4) 50%, rgba(236,72,153,0.3) 100%)',borderRadius:'14px',border:'2px solid rgba(167,139,250,0.6)',textAlign:'center',boxShadow:'0 0 30px rgba(124,58,237,0.5), 0 0 60px rgba(124,58,237,0.2)',animation:'pendingPulse 2s ease-in-out infinite',position:'relative',overflow:'hidden'}}>
          <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(59,130,246,0.1))',animation:'gradientPulse 3s ease infinite'}}></div>
          <div style={{position:'relative',zIndex:1}}>
            <div style={{color:'#e2e8f0',fontSize:'16px',fontWeight:'800',letterSpacing:'0.3px',marginBottom:'6px'}}>
              ⚙️ Your batch is being processed!
            </div>
            <div style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'600'}}>
              We'll notify you by email when ready — or wait here and they'll appear automatically ✨
            </div>
            {funMessage && (
              <div style={{marginTop:'12px',padding:'10px 16px',background:'rgba(0,0,0,0.2)',borderRadius:'10px',color:'#f1f5f9',fontSize:'14px',fontWeight:'700'}}>
                {funMessage}
              </div>
            )}
          </div>
        </div>
      )}
      {/* MOBILE ONLY: Sticky queue/processing banner at top — scrolls to full progress panel */}
      {progress.status !== 'idle' && batchResults.length === 0 && (
        <div className="mobile-progress-sticky" onClick={() => progressRef.current && progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
            <span style={{fontSize:'14px',fontWeight:'700',color:'#c4b5fd',flex:1}}>{progress.message}</span>
            <span style={{fontSize:'12px',color:'#a78bfa',fontWeight:'700',whiteSpace:'nowrap'}}>{Math.round(batchProgress.overall > 0 ? batchProgress.overall : progress.progress)}%</span>
          </div>
          <div style={{height:'4px',background:'rgba(255,255,255,0.1)',borderRadius:'2px',marginTop:'8px',overflow:'hidden'}}>
            <div style={{height:'100%',background:'linear-gradient(90deg,#7c3aed,#06b6d4)',borderRadius:'2px',width:(batchProgress.overall > 0 ? batchProgress.overall : progress.progress)+'%',transition:'width 0.5s'}}></div>
          </div>
        </div>
      )}

      <div style={{padding:"0 0 8px 0"}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:"dashboard"}, "", "/"); window.dispatchEvent(new PopStateEvent("popstate", {state:{section:"dashboard"}})); }}>🏠 ← Dashboard</button></div>
      <div className="batch-header">
        <h3>🎬 Batch Video Processing</h3>
        <p>Process up to {user && user.plan === 'business' ? 4 : 3} YouTube videos at once - Perfect for content creators!</p>
        <div className="batch-limits-info">
          <small>
            📊 <strong>Maximum URLs:</strong> {user && user.plan === 'business' ? 4 : 3} videos per batch |
            🎬 <strong>Clips per video:</strong> {user && user.plan === 'business' ? 8 : user && user.plan === 'pro' ? 5 : 3} |
            ⏱️ <strong>Processing Time:</strong> varies by video length
          </small>
        </div>
      </div>

      <div className="batch-controls">
        <div className="duration-control">
          <label>Clip Duration: </label>
          <select
            value={customDuration}
            onChange={(e) => setCustomDuration(parseInt(e.target.value))}
            disabled={isProcessing}
          >
            <option value={15}>15 seconds</option>
            <option value={20}>20 seconds</option>
            <option value={30}>30 seconds</option>
            {user.plan === 'business' && <option value={60}>60 seconds</option>}
          </select>
        </div>
        
        <div style={{margin:'0 0 14px 0',padding:'10px 16px',background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.2)',borderRadius:'10px',display:'flex',alignItems:'flex-start',gap:'10px'}}>
          <span style={{fontSize:'14px',flexShrink:0,marginTop:'1px'}}>⚠️</span>
          <p style={{color:'#fbbf24',fontSize:'12px',fontWeight:'600',margin:0,lineHeight:'1.6',opacity:0.9}}>Some videos may not process if they are restricted, private, age-gated, or part of a playlist. Use direct video URLs for best results.</p>
        </div>
        {validateUrls().length > 0 && !isProcessing && (
          <div style={{background:'rgba(99,102,241,0.15)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'10px', padding:'10px 16px', margin:'0 0 12px 0', textAlign:'center'}}>
            <p style={{color:'#a5b4fc', fontSize:'13px', margin:0, lineHeight:'1.5'}}>
              {(() => {
                const numVids = validateUrls().length;
                const clipSec = customDuration || 15;
                const isBiz = user && user.plan === 'business';
                const clipsPerVid = isBiz ? 8 : user.plan === 'pro' ? 5 : 3;
                return '⏱️ ' + numVids + ' video' + (numVids > 1 ? 's' : '') + ' × ' + clipsPerVid + ' clips at ' + clipSec + 's each. Processing time depends on video length — short videos (~5 min) are faster than long ones (~30+ min).';
              })()}
              <br/><span style={{fontSize:'11px', opacity:0.8}}>You can leave this page open — processing continues in the background.</span>
            </p>
          </div>
        )}
      {batchErrors.length > 0 && (
        <div id="batch-error-msg" className="batch-errors">
          <h4>⚠️ Processing Errors</h4>
          {batchErrors.map((error, index) => (
            <div key={index} className="error-card">
              <strong>Video {error.videoIndex}:</strong> {error.error}
              <br />
              <small>{error.videoUrl}</small>
            </div>
          ))}
        </div>
      )}
      {/* Limit/Error Banner - shows above process button */}
      {progress.message && (progress.message.includes('limit') || progress.message.includes('failed') || progress.message.includes('error')) && progress.status !== 'idle' && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: '10px',
          padding: '12px 16px',
          margin: '12px 0',
          textAlign: 'center',
          color: '#fca5a5',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          ⚠️ {progress.message}
        </div>
      )}
        <div className="aspect-ratio-picker" style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'10px',padding:'12px 20px',background:'rgba(124,58,237,0.1)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.3)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto',flexWrap:'wrap'}}>
            <span style={{display:'flex',alignItems:'center',gap:'8px'}}><span style={{fontSize:'18px'}}>📐</span><span style={{color:'#f1f5f9',fontSize:'14px',fontWeight:'700',whiteSpace:'nowrap'}}>Aspect Ratio</span></span>
            <div style={{display:'flex',gap:'6px'}}>
              <button onClick={() => { if (!isProcessing && !isSubmitting) setAspectRatio('9:16'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '9:16' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '9:16' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '9:16' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>9:16</button>
              <button onClick={() => { if (!isProcessing && !isSubmitting) setAspectRatio('4:5'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '4:5' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '4:5' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '4:5' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>4:5</button>
              <button onClick={() => { if (!isProcessing && !isSubmitting) setAspectRatio('1:1'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '1:1' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '1:1' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '1:1' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>1:1</button>
            </div>
            <span style={{color:'#64748b',fontSize:'11px',fontWeight:'600',whiteSpace:'nowrap'}}>{aspectRatio === '9:16' ? 'TikTok, Reels, Shorts' : aspectRatio === '4:5' ? 'Instagram Feed, Facebook' : 'Instagram, LinkedIn, X'}</span>
          </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'14px',padding:'12px 24px',background: hookEnabled ? 'rgba(236,72,153,0.15)' : 'rgba(124,58,237,0.1)',borderRadius:'12px',border: hookEnabled ? '1px solid rgba(236,72,153,0.4)' : '1px solid rgba(124,58,237,0.3)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
            <span style={{fontSize:'20px'}}>🪝</span>
            <span style={{color:'#f1f5f9',fontSize:'15px',fontWeight:'700',letterSpacing:'0.3px'}}>AI Hook</span>
            <div
              onClick={() => {
                if (isProcessing || isSubmitting) return;
                setHookEnabled(prev => !prev);
              }}
              style={{
                width:'56px',height:'30px',borderRadius:'15px',
                backgroundColor: hookEnabled ? '#ec4899' : '#334155',
                cursor:'pointer',position:'relative',transition:'all 0.3s ease',
                border: hookEnabled ? '2px solid #f472b6' : '2px solid #475569',
                boxShadow: hookEnabled ? '0 0 12px rgba(236,72,153,0.4)' : 'none'
              }}
            >
              <div style={{
                width:'22px',height:'22px',borderRadius:'50%',backgroundColor:'white',
                position:'absolute',top:'2px',left: hookEnabled ? '30px' : '2px',
                transition:'left 0.3s ease',boxShadow:'0 2px 4px rgba(0,0,0,0.3)'
              }}></div>
            </div>
            <span style={{color: hookEnabled ? '#f472b6' : '#64748b',fontSize:'14px',fontWeight:'700',minWidth:'30px'}}>
              {hookEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          {hookEnabled && (
            <div style={{margin:'-8px 0 12px 0',textAlign:'center'}}>
              <span style={{color:'#f472b6',fontSize:'12px',fontWeight:'600'}}>AI generates an attention-grabbing hook overlay on the first 3 seconds of each clip</span>
            </div>
          )}
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'14px',padding:'12px 24px',background:'rgba(124,58,237,0.1)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.3)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
            <span style={{fontSize:'20px'}}>📝</span>
            <span style={{color:'#f1f5f9',fontSize:'15px',fontWeight:'700',letterSpacing:'0.3px'}}>AI Captions</span>
            <div 
              onClick={() => { if (!isProcessing && !isSubmitting) setCaptionsEnabled(prev => !prev); }}
              style={{
                width:'56px',height:'30px',borderRadius:'15px',
                backgroundColor: captionsEnabled ? '#7c3aed' : '#334155',
                cursor:'pointer',position:'relative',transition:'all 0.3s ease',
                border: captionsEnabled ? '2px solid #a78bfa' : '2px solid #475569',
                boxShadow: captionsEnabled ? '0 0 12px rgba(124,58,237,0.4)' : 'none'
              }}
            >
              <div style={{
                width:'22px',height:'22px',borderRadius:'50%',backgroundColor:'white',
                position:'absolute',top:'2px',left: captionsEnabled ? '30px' : '2px',
                transition:'left 0.3s ease',boxShadow:'0 2px 4px rgba(0,0,0,0.3)'
              }}></div>
            </div>
            <span style={{color: captionsEnabled ? '#a78bfa' : '#64748b',fontSize:'14px',fontWeight:'700',minWidth:'30px'}}>
              {captionsEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🌍</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Subtitle Language</span>
                <select
                  value={subtitleLanguage}
                  onChange={(e) => setSubtitleLanguage(e.target.value)}
                  style={{
                    background:'rgba(30,41,59,0.9)',
                    color:'#f1f5f9',
                    border:'1px solid rgba(124,58,237,0.4)',
                    borderRadius:'8px',
                    padding:'6px 12px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor:'pointer',
                    outline:'none',
                    flex:'1',
                    minWidth:'140px',
                    maxWidth:'200px'
                  }}
                >
                  <option value="auto">🔍 Auto-detect</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                  <option value="fr">🇫🇷 French</option>
                  <option value="de">🇩🇪 German</option>
                  <option value="pt">🇵🇹 Portuguese</option>
                  <option value="it">🇮🇹 Italian</option>
                  <option value="nl">🇳🇱 Dutch</option>
                  <option value="ar">🇸🇦 Arabic</option>
                  <option value="hi">🇮🇳 Hindi</option>
                  <option value="ja">🇯🇵 Japanese</option>
                  <option value="ko">🇰🇷 Korean</option>
                  <option value="zh">🇨🇳 Chinese</option>
                  <option value="ru">🇷🇺 Russian</option>
                </select>
              </div>
              {subtitleLanguage !== 'auto' && (
                <p style={{color:'#64748b',fontSize:'11px',textAlign:'center',margin:'8px 0 0 0'}}>
                  ⚠️ Only change if auto-detect picks the wrong language
                </p>
              )}
            </div>
          )}
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background: translateLanguage ? 'rgba(6,182,212,0.12)' : 'rgba(124,58,237,0.08)',borderRadius:'12px',border: translateLanguage ? '1px solid rgba(6,182,212,0.4)' : '1px solid rgba(124,58,237,0.2)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🌐</span>
                <span style={{color:'#67e8f9',fontSize:'13px',fontWeight:'700'}}>Translate Captions</span>
                <select
                  value={translateLanguage}
                  onChange={(e) => setTranslateLanguage(e.target.value)}
                  style={{
                    background:'rgba(30,41,59,0.9)',
                    color:'#f1f5f9',
                    border:'1px solid rgba(6,182,212,0.4)',
                    borderRadius:'8px',
                    padding:'6px 12px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor:'pointer',
                    outline:'none',
                    flex:'1',
                    minWidth:'140px',
                    maxWidth:'200px'
                  }}
                >
                  <option value="">None (Original)</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                  <option value="fr">🇫🇷 French</option>
                  <option value="de">🇩🇪 German</option>
                  <option value="pt">🇵🇹 Portuguese</option>
                  <option value="it">🇮🇹 Italian</option>
                  <option value="nl">🇳🇱 Dutch</option>
                  <option value="ar">🇸🇦 Arabic</option>
                  <option value="hi">🇮🇳 Hindi</option>
                  <option value="ja">🇯🇵 Japanese</option>
                  <option value="ko">🇰🇷 Korean</option>
                  <option value="zh">🇨🇳 Chinese</option>
                  <option value="ru">🇷🇺 Russian</option>
                  <option value="tr">🇹🇷 Turkish</option>
                  <option value="pl">🇵🇱 Polish</option>
                  <option value="sv">🇸🇪 Swedish</option>
                  <option value="no">🇳🇴 Norwegian</option>
                  <option value="da">🇩🇰 Danish</option>
                  <option value="fi">🇫🇮 Finnish</option>
                  <option value="el">🇬🇷 Greek</option>
                  <option value="cs">🇨🇿 Czech</option>
                  <option value="ro">🇷🇴 Romanian</option>
                  <option value="hu">🇭🇺 Hungarian</option>
                  <option value="th">🇹🇭 Thai</option>
                  <option value="vi">🇻🇳 Vietnamese</option>
                  <option value="id">🇮🇩 Indonesian</option>
                  <option value="ms">🇲🇾 Malay</option>
                  <option value="uk">🇺🇦 Ukrainian</option>
                  <option value="he">🇮🇱 Hebrew</option>
                  <option value="bn">🇧🇩 Bengali</option>
                  <option value="lt">🇱🇹 Lithuanian</option>
                </select>
              </div>
              {translateLanguage && (
                <p style={{color:'#67e8f9',fontSize:'11px',textAlign:'center',margin:'8px 0 0 0',fontWeight:'600'}}>
                  Captions will be translated — 30 languages supported
                </p>
              )}
            </div>
          )}
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>✨</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Caption Style</span>
                <CaptionStylePicker
                  value={captionStyle}
                  onChange={setCaptionStyle}
                  userPlan={user.plan}
                  onLockedClick={(name) => setUpgradeModal({ open: true, plan: 'pro', feature: name })}
                />
              </div>
            </div>
          )}
          {captionsEnabled && (user.plan === 'pro' || user.plan === 'business') && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🎨</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Highlight Colour</span>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {[
                    {name:'yellow',hex:'#FFFF00'},
                    {name:'green',hex:'#00FF00'},
                    {name:'cyan',hex:'#00FFFF'},
                    {name:'pink',hex:'#FF60CB'},
                    {name:'red',hex:'#FF0000'},
                    {name:'orange',hex:'#FF8000'}
                  ].map(c => (
                    <div
                      key={c.name}
                      onClick={() => setCaptionColour(c.name)}
                      style={{
                        width:'28px',height:'28px',borderRadius:'50%',
                        backgroundColor:c.hex,cursor:'pointer',
                        border: captionColour === c.name ? '3px solid white' : '2px solid rgba(255,255,255,0.2)',
                        boxShadow: captionColour === c.name ? '0 0 12px ' + c.hex : 'none',
                        transition:'all 0.2s ease'
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
              <span style={{fontSize:'16px'}}>😍</span>
              <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Inline Emoji</span>
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                {['auto','','🔥','😂','💯','😍','🎯','💪','🤯','👀','❤️','⚡'].map(e => (
                  <div
                    key={e || 'off'}
                    onClick={() => {
                      if (e && e !== '' && user.plan !== 'pro' && user.plan !== 'business') {
                        setUpgradeModal({ open: true, plan: 'pro', feature: 'Inline Emoji Captions' });
                        return;
                      }
                      setEmojiOverlay(e);
                    }}
                    style={{
                      width: e === 'auto' ? 'auto' : '34px', height:'34px',borderRadius:'8px',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize: (e && e !== 'auto') ? '18px' : '11px',
                      padding: e === 'auto' ? '0 10px' : '0',
                      background: emojiOverlay === e ? 'rgba(124,58,237,0.4)' : 'rgba(30,41,59,0.6)',
                      border: emojiOverlay === e ? '2px solid #a78bfa' : '1px solid rgba(255,255,255,0.1)',
                      cursor:'pointer',transition:'all 0.2s ease',
                      boxShadow: emojiOverlay === e ? '0 0 10px rgba(124,58,237,0.5)' : 'none'
                    }}
                  >{e === 'auto' ? <span style={{color:'#34d399',fontWeight:'700'}}>Auto</span> : e || <span style={{color:'#64748b'}}>Off</span>}</div>
                ))}
              </div>
            </div>
          </div>
        <div className="batch-actions">
          <button
            onClick={handleBatchProcess}
            disabled={isProcessing || isSubmitting || (batchMode === 'url' ? validateUrls().length === 0 : batchUploadPaths.filter(p => p).length === 0)}
            className="process-btn"
          >
            {isProcessing ? 'Processing...' : isSubmitting ? 'Submitting...' : `Process ${batchMode === 'url' ? validateUrls().length : batchUploadPaths.filter(p => p).length} Videos`}
          </button>
          
          {isProcessing && (
            <button 
              onClick={handleCancelBatch} 
              className="cancel-btn"
              title="Cancel batch processing"
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                color: '#ffffff',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                animation: 'cancelPulse 2s ease-in-out infinite'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 30px rgba(239, 68, 68, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(239, 68, 68, 0.3)';
              }}
            >
              <span style={{
                fontSize: '16px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}>⚡</span>
              <span>Cancel Batch</span>
            </button>
          )}
          
          {!isProcessing && (
            <button onClick={clearAll} className="clear-btn">Clear All</button>
          )}
        </div>
      </div>

      {/* Top mode toggle */}
      <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'20px',padding:'16px',background:'rgba(255,255,255,0.03)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.15)'}}>
        <span style={{color:'#94a3b8',fontSize:'13px',fontWeight:'600'}}>Input mode:</span>
        <div style={{display:'flex',borderRadius:'10px',overflow:'hidden',border:'1px solid rgba(124,58,237,0.3)',opacity:(isProcessing||isSubmitting)?0.5:1,pointerEvents:(isProcessing||isSubmitting)?'none':'auto'}}>
          <button onClick={() => { if(isProcessing||isSubmitting) return; setBatchMode('url'); setBatchFiles(Array(6).fill(null)); setBatchUploadPaths(Array(6).fill('')); setBatchUploadProgress(Array(6).fill(0)); }} style={{padding:'8px 20px',background:batchMode==='url'?'rgba(124,58,237,0.35)':'transparent',color:batchMode==='url'?'#c4b5fd':'#64748b',border:'none',cursor:(isProcessing||isSubmitting)?'not-allowed':'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s'}}>🔗 YouTube URLs</button>
          {(user.plan === 'pro' || user.plan === 'business') && (
            <button onClick={() => { if(isProcessing||isSubmitting) return; setBatchMode('upload'); setBatchUrls(Array(6).fill('')); }} style={{padding:'8px 20px',background:batchMode==='upload'?'rgba(124,58,237,0.35)':'transparent',color:batchMode==='upload'?'#c4b5fd':'#64748b',border:'none',cursor:(isProcessing||isSubmitting)?'not-allowed':'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s'}}>📁 My Videos</button>
          )}
        </div>
        {batchMode==='upload' && <span style={{color:'#64748b',fontSize:'12px'}}>Max {user.plan==='business'?'5GB':'2GB'} per video · {user.plan==='business'?'3':'2'} videos max · Files deleted after processing</span>}
      </div>

      <div className="batch-inputs">
        {(batchMode === 'upload'
          ? Array(user.plan === 'business' ? 3 : 2).fill('')
          : batchUrls.slice(0, urlSlots)
        ).map((url, index) => {
          const isValid = isValidYouTubeUrl(url);
          const isDuplicate = isDuplicateUrl(url, index);
          
          return (
            <div key={index} className={`batch-input-row ${isDuplicate ? 'duplicate' : ''}`}>
              <span className="input-label">Video {index + 1}:</span>
              {batchMode === 'url' ? (
                <div className="input-with-validation" style={{flex:1}}>
                  <input type="url" placeholder={`YouTube URL ${index + 1} (optional)`} value={url} onChange={(e) => handleUrlChange(index, e.target.value)} className={`batch-url-input ${isValid === false ? 'invalid' : ''} ${isDuplicate ? 'duplicate' : ''}`} disabled={isProcessing} />
                  <div className="validation-indicators">
                    {isValid === true && !isDuplicate && <span className="valid-icon">✓</span>}
                    {isValid === false && <span className="invalid-icon clickable" onClick={() => handleUrlChange(index, '')} title="Clear field">✗</span>}
                    {isDuplicate && <span className="duplicate-icon clickable" onClick={() => handleUrlChange(index, '')} title="Click to clear" style={{cursor:'pointer'}}>✗</span>}
                  </div>
                </div>
              ) : (
                <div style={{flex:1}}>
                  {!batchFiles[index] ? (
                    <div onDragOver={(e)=>{e.preventDefault();e.currentTarget.style.borderColor='#7c3aed';}} onDragLeave={(e)=>{e.currentTarget.style.borderColor='rgba(124,58,237,0.3)';}} onDrop={(e)=>{e.preventDefault();e.currentTarget.style.borderColor='rgba(124,58,237,0.3)';handleBatchFileUpload(index,e.dataTransfer.files[0]);}} onClick={()=>document.getElementById(`batchFile${index}`).click()} style={{border:'2px dashed rgba(124,58,237,0.3)',borderRadius:'10px',padding:'14px',textAlign:'center',cursor:'pointer',background:'rgba(124,58,237,0.05)',transition:'all 0.2s'}}>
                      <input id={`batchFile${index}`} type="file" accept=".mp4,.mov,.avi,.mkv,.webm" style={{display:'none'}} onChange={(e)=>handleBatchFileUpload(index,e.target.files[0])} />
                      <div style={{color:'#94a3b8',fontSize:'12px'}}>📁 Drop video or click to browse</div>
                    </div>
                  ) : (
                    <div style={{background:'rgba(124,58,237,0.1)',borderRadius:'10px',padding:'10px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div style={{fontSize:'12px',color:'#c4b5fd'}}>
                        {batchUploadProgress[index] < 100 ? `⏫ Uploading... ${batchUploadProgress[index]}%` : `✅ ${batchFiles[index].name}`}
                      </div>
                      <button onClick={()=>clearBatchFile(index)} style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',color:'#f87171',padding:'3px 8px',borderRadius:'6px',cursor:'pointer',fontSize:'11px'}}>✕</button>
                    </div>
                  )}
                </div>
              )}
              {isDuplicate && batchMode === 'url' && <div className="duplicate-warning"><small>Duplicate URL - only one instance will be processed</small></div>}
            </div>
          );
        })}
      </div>

      {/* PROGRESS PANEL — bottom of page, auto-scrolls on mobile */}
      {progress.status !== 'idle' && batchResults.length === 0 && (
        <div className="batch-progress" ref={progressRef}>
          <div className="progress-header">
            <h4>Processing Progress</h4>
            {batchProgress.total > 0 && (
              <span className="batch-counter">
                Video {Math.min(batchProgress.current + 1, batchProgress.total)} of {batchProgress.total}
              </span>
            )}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${batchProgress.overall > 0 ? batchProgress.overall : progress.progress}%` }}
            ></div>
          </div>
          <p className="progress-message" style={progress.step === 'queued' ? {background:'linear-gradient(135deg,rgba(124,58,237,0.2),rgba(59,130,246,0.2))',border:'1px solid rgba(124,58,237,0.4)',borderRadius:'12px',padding:'14px 18px',fontSize:'15px',fontWeight:'700',color:'#c4b5fd',textAlign:'center',animation:'pulse 2s ease-in-out infinite',boxShadow:'0 0 20px rgba(124,58,237,0.3)'} : {}}>{progress.message}</p>
          <div style={{margin:'8px 0',padding:'10px 16px',background:'rgba(16,185,129,0.1)',borderRadius:'10px',border:'1px solid rgba(16,185,129,0.2)',textAlign:'center'}}>
            <div style={{color:'#34d399',fontSize:'13px',fontWeight:'600'}}>
              ✉️ You can close this tab — we'll email you when your clips are ready!
            </div>
          </div>
          <div className="progress-percentage">
            {Math.round(batchProgress.overall > 0 ? batchProgress.overall : progress.progress)}% Complete
          </div>
          {funMessage && (
            <div style={{marginTop:'16px',padding:'16px 20px',background:'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(59,130,246,0.25) 50%, rgba(236,72,153,0.25) 100%)',borderRadius:'14px',border:'1px solid rgba(167,139,250,0.3)',textAlign:'center'}}>
              <div style={{color:'#f1f5f9',fontSize:'15px',fontWeight:'700'}}>{funMessage}</div>
            </div>
          )}
        </div>
      )}

      {batchResults.length > 0 && (() => {
        // Flatten all clips from all batch results for the shared component
        const allBatchClips = batchResults.filter(r => !r.isDemo && !r.isDemoResult).flatMap(r => (r.clips || []).map(c => ({ ...c, file: c.file || c.videoUrl })));
        return allBatchClips.length > 0 ? (
          <ClipResultsSection
            clips={allBatchClips}
            plan={user.plan}
            onWatch={(clip) => { if (clip.file) window.open(clip.file, '_blank'); }}
            onDownload={handleDownload}
            onRefresh={clearResults}
            getEngagementExplanation={getEngagementExplanation}
            title={allBatchClips.length + ' Clips from ' + batchResults.length + ' Video' + (batchResults.length > 1 ? 's' : '')}
          />
        ) : null;
      })()}

      {/* Floating button is now built into ClipResultsSection */}
    </div>
  );
};

export default BatchProcessor;